import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_URL } from "constants";

export const captainNapalmApi = createApi({
  reducerPath: "captainNapalmApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_URL}/v2`,
  }),
  tagTypes: ["ProcessingJobs", "ProcessingResults", "SceneResults", "Videos"],
  endpoints: (builder) => ({
    // Submit Video Processing Job
    submitVideoProcessing: builder.mutation({
      query: ({ token, videoUrls }) => ({
        url: `/${token}/cn/index`,
        method: "POST",
        body: videoUrls ? { video_urls: videoUrls } : {},
      }),
      invalidatesTags: ["ProcessingJobs"],
    }),

    // Get Processing Status
    getProcessingStatus: builder.query({
      query: ({ token, jobId }) => `/${token}/cn/status/${jobId}`,
      providesTags: (result, error, { jobId }) => [
        { type: "ProcessingJobs", id: jobId },
      ],
    }),

    // Get Processing Results
    getProcessingResults: builder.query({
      query: ({ token, jobId }) => `/${token}/cn/results/${jobId}`,
      providesTags: (result, error, { jobId }) => [
        { type: "ProcessingResults", id: jobId },
      ],
    }),

    // Download Processing Results
    downloadProcessingResults: builder.query({
      query: ({ token, jobId }) => `/${token}/cn/results/${jobId}/download`,
      responseHandler: (response) => response.blob(),
    }),

    // List Processing Jobs
    getProcessingJobs: builder.query({
      query: ({ token, page = 1, limit = 10, status }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        if (status) {
          params.append("status", status);
        }
        return `/${token}/cn/jobs?${params.toString()}`;
      },
      providesTags: ["ProcessingJobs"],
    }),

    // Generate Scene
    generateScene: builder.mutation({
      query: ({ token, sceneData }) => ({
        url: `/${token}/cn/scene/generate`,
        method: "POST",
        body: {
          scene_name: sceneData.title,
          scene_prompt: sceneData.prompt,
        },
      }),
      invalidatesTags: ["SceneResults"],
    }),

    // List Scene Results
    getSceneResults: builder.query({
      query: (token) => `/${token}/cn/scene/results`,
      providesTags: ["SceneResults"],
    }),

    // Get Scene Result
    getSceneResult: builder.query({
      query: ({ token, sceneName }) =>
        `/${token}/cn/scene/results/${sceneName}`,
      providesTags: (result, error, { sceneName }) => [
        { type: "SceneResults", id: sceneName },
      ],
    }),

    // Convert Scene to Video
    convertSceneToVideo: builder.mutation({
      query: ({ token, sceneName }) => ({
        url: `/${token}/cn/scene/convert-to-video`,
        method: "POST",
        body: { scene_name: sceneName },
      }),
      invalidatesTags: ["Videos"],
    }),
  }),
});

export const {
  useSubmitVideoProcessingMutation,
  useGetProcessingStatusQuery,
  useGetProcessingResultsQuery,
  useDownloadProcessingResultsQuery,
  useGetProcessingJobsQuery,
  useGenerateSceneMutation,
  useGetSceneResultsQuery,
  useGetSceneResultQuery,
  useConvertSceneToVideoMutation,
} = captainNapalmApi;

export default captainNapalmApi;
